import styles from './page-erfa-request.module.scss';
import { FC, SetStateAction, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import AppLayout from '../../components/layout/app-layout/app-layout';
import BackButton from '../../components/shared/BackButton/BackButton';
import BtButton from '../bt-button/bt-button';
import Collapsible from '../../components/shared/Collapsible';
import Editor from '../../components/shared/Editor/Editor';
import TitleWrapper from '../../components/shared/TitleWrapper/TitleWrapper';
import DraggableDropdown from '../../components/shared/DraggableDropdown/draggable-dropdown';
import AddCircle from '@material-ui/icons/AddCircle';
import { OptionType } from '../../models/global.model';
import { ServiceErfa } from '../../services/service-erfa';
import { ServiceUser } from '../../services/service-user';
import { TrashIcon } from '../../components/svgs';
import StyledCheckbox from '../../components/shared/StyledCheckbox/StyledCheckbox';
import AddRecipientDialog from '../../components/shared/AddRecipientDialog/AddRecipientDialog';
import { UserOptionType } from '../../components/shared/UserSelector/UserSelector';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import { NotificationManager } from "react-notifications";
import EmailPreview from '../../components/shared/EmailPreview/EmailPreview';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

type Params = {
  areaSlug: string;
  project: string;
  version: string;
};

type RouteTypes = RouteComponentProps<Params, Record<string, unknown>>;

type Props = {
  history: RouteTypes['history'];
  match: RouteTypes['match'];
  currentProject: CurrentProjectType;
}
type Area = {
  reward: boolean,
  areaName: string,
  areaId: string,
  supplierId: {
    _id: string,
    name: string
  }
};
type CurrentProjectType = {
  area: {
    areaId: {
      _id: string,
      name: string
    }
    suppliers: Area[]
  }[],
  name: string,
  slug: string,
}

export const getValidationMessage = (areas: string[], recipients: UserOptionType[], questions: OptionType[], t: TFunction<'projects', 'erfaRequest'>) => {
  const validationMessagesArray = [];
  if (areas?.length === 0) {
    validationMessagesArray.push(t('validation.areas', 'minst en leverantör att utvärdera'));
  }
  if (recipients?.length === 0) {
    validationMessagesArray.push(t('validation.recipients', 'minst en mottagare'));
  }
  if (questions?.length === 0) {
    validationMessagesArray.push(t('validation.questions', 'minst en fråga'));
  }
  const questionCounts = questions.map(o => o?.label.toLowerCase().trim() ?? "").reduce((map, key) => map.set(key, (map.get(key) ?? 0) + 1), new Map<string, number>());
  const duplicateQuestions = Array.from(questionCounts.values()).find(count => count > 1);
  const emptyQuestions = questionCounts.get("") ?? 0;

  const validationMessage = [
    validationMessagesArray.length ? t('validation.emptyFields', 'Välj {{validationMessages_list}}.', { validationMessages: validationMessagesArray }) : null,
    duplicateQuestions ? t('validation.duplicateQuestions', 'Samma fråga finns med flera gånger.') : null,
    emptyQuestions > 0 ? t('validation.emptyQuestions', 'Flera frågor är tomma.', { count: emptyQuestions }) : null,
  ].filter(x => x).join(' ');

  return validationMessage;
}

const PageErfaRequest: FC<Props> = ({history, match, currentProject}) => {
  const [t] = useTranslation('projects', { keyPrefix: 'erfaRequest' });
  const [questions, setQuestions] = useState<OptionType[]>([]);
  const [questionsOpen, setQuestionsOpen] = useState(true);
  const [questionOptions, setQuestionOptions] = useState<OptionType[]>([]);
  const [recipientsOpen, setRecipientsOpen] = useState(true);
  const [recipients, setRecipients] = useState<(UserOptionType[])>([]);
  const [userOptions, setUserOptions] = useState<UserOptionType[]>([]);
  const [showAddRecipient, setShowAddRecipient] = useState(false);
  const [addRecipientDialogKey, setAddRecipientDialogKey] = useState(0);
  const [areAreasOpen, setAreAreasOpen] = useState(true);
  const [areas, setAreas] = useState<Area[]>([]);
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [isEmailOpen, setIsEmailOpen] = useState(true);
  const [emailText, setEmailText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchQuestions = async () => {
    const options = await ServiceErfa.getQuestions();
    setQuestionOptions(options.map((o: {_id: string, question: string }) => ({value: o._id, label: o.question})));
  };

  const fetchUsers = async () => {
    const roles = await ServiceUser.getUsersWithRoles();
    const users = roles.length && roles.reduce((arr : UserOptionType[], elem : {users : UserOptionType[]}) => [...arr, ...elem.users], []);
    const people = users.map((user: UserOptionType) => ({ ...user, label: user.name, value: user._id }));
    setUserOptions(people);
  }

  useEffect(() => {
    (async () => {
      await fetchQuestions();
      await fetchUsers();
    })();
  }, [])

  useEffect(() => {
    const temp: Area[] = [];
    currentProject.area.forEach(area => area.suppliers.forEach(supplier => {
      if (supplier.reward) temp.push({...supplier, areaId: area.areaId._id, areaName: area.areaId.name});
    }));
    setAreas(temp);
  }, [currentProject])

  const goBack = () => {
    const url = match.url.split('/');
    url.splice(4);
    history.push(url.join('/'));
  };

  const renderSuppliers = () => {
    const isAreaSelected = (id: string) => selectedAreas.includes(id);

    return <Collapsible
      header={<strong>{t('suppliers.heading', 'Välj leverantörer som ska utvärderas')}</strong>}
      isOpen={areAreasOpen}
      onChangeCollapse={() => setAreAreasOpen(oldState => !oldState)}
    >
      <div className={styles.supplierList}>
        {areas.map(area => area.areaId &&
          <label key={area.areaId}>
            <StyledCheckbox
              checked={isAreaSelected(area.areaId)}
              onChange={() => {
                setSelectedAreas(oldState => {
                  let temp = [...oldState];
                  const supplierId = area.areaId;
                  if (temp.includes(supplierId)) {
                    temp = temp.filter(id => id !== supplierId)
                  } else {
                    temp.push(supplierId);
                  }
                  return temp;
                })
              }}
            />
            <div>
              <span>{area.areaName}</span>
              <span>{area.supplierId.name}</span>
            </div>
          </label>
        )}
      </div>
    </Collapsible>;
  };

  const renderRecipients = () => {
    const onAddRecipient = (user: UserOptionType) => {
      setRecipients(prev => [...prev, user]);
    }
    const removeRecipient = (user: UserOptionType) => {
      setRecipients(prev =>  prev.filter(x=>x._id != user._id));
    }
    const showAddRecipientModal = () => {
      setAddRecipientDialogKey(prev => prev + 1);
      setShowAddRecipient(true);
    }
    return <Collapsible
      header={<strong>{t('recipients.heading', '{{count}} mottagare', { count: recipients?.length ?? 0 })}</strong>}
      isOpen={recipientsOpen}
      onChangeCollapse={() => setRecipientsOpen(prev => !prev)}
      contentContainerClassName={styles.recipients}
    >
      {recipients?.map(recipient =>
        <div key={recipient.email} className={styles.recipient}>
          <div>
            <strong>{recipient.name}</strong><br/>
            <span>{recipient.email}</span>
          </div>
          <div>
            <BtButton color="white" size="xxs" onClick={() => removeRecipient(recipient)}>
              <TrashIcon size="16px" />
            </BtButton>
          </div>
        </div>
      )}
      <BtButton class='white' size='xxs' onClick={showAddRecipientModal}>
        <AddCircle style={{ fontSize: '16px', fill: 'var(--orange-500)' }} />
        <span>{t('recipients.button.add', 'Lägg till mottagare')}</span>
      </BtButton>
      <AddRecipientDialog
        show={showAddRecipient}
        setShow={setShowAddRecipient}
        recipients={recipients}
        userOptions={userOptions}
        onSave={onAddRecipient}
        key={addRecipientDialogKey}
      />
    </Collapsible>;
  }

  const renderQuestions = () => {
    const list = questions;
    const addField = (
      _list: OptionType[],
      setList: (value: SetStateAction<OptionType[]>) => void
    ) => {
      setList((prevState: OptionType[]) => [...prevState, { label: '', value: Date.now().toString() }]);
    };

    return <Collapsible
      header={<strong>{t('questions.heading', 'Utvärdering')}</strong>}
      isOpen={questionsOpen}
      onChangeCollapse={() => setQuestionsOpen(prev => !prev)}
      contentContainerClassName={styles.questions}
    >
      <TitleWrapper title={t('questions.label', 'VÄLJ FRÅGOR')} className={styles.titleWrapper}>
        <div style={{ position: 'relative' }}>
          <div style={{ marginLeft: '-32px', marginRight: '-32px' }}>
            <DraggableDropdown
              options={questionOptions}
              list={list}
              onChange={setQuestions}
            />
          </div>

          <BtButton class='white' size='xxs' onClick={() => addField(list, setQuestions)}>
            <AddCircle style={{ fontSize: '16px', fill: 'var(--orange-500)' }} />
            <span>{t('questions.button.add', 'Lägg till fråga')}</span>
          </BtButton>
        </div>
      </TitleWrapper>
    </Collapsible>;
  }

  const renderEmailViewer = () => {
    const projectName = currentProject.name;

    return <Collapsible
      header={<strong>{t('email.heading', 'E-postmeddelande')}</strong>}
      isOpen={isEmailOpen}
      onChangeCollapse={() => setIsEmailOpen(oldState => !oldState)}
    >
      <div className={styles.emailContainer}>
        <div>
          <p className={styles.bold} style={{ marginBottom: '10px' }}>{t('email.template.message.title', 'Meddelande från avsändaren (valfritt):')}</p>
          <Editor value={emailText} onChange={setEmailText} wrapperStyle={{ height: '400px' }} />
        </div>
        <EmailPreview
          type="erfa"
          params={{
            projectName,
            areaName: '<yrkeskategori>',
            supplierName: '<leverantör>',
            emailText,
          }}
        />
      </div>
    </Collapsible>;
  }

  const validationMessage = getValidationMessage(selectedAreas, recipients, questions, t);

  const handleSubmit = async () => {
    setSubmitting(true);
    const suppliers = selectedAreas.map(selectedAreaId => areas.find(area => area.areaId === selectedAreaId));

    const _questions = questions.map(q => {
      const existing = questionOptions.find(o => o?.label === q?.label);
      return {
        value: existing?.value ?? "new",
        label: q?.label,
        __isNew__: !existing,
      };
    });

    const erfaObj = {
      questions: _questions,
      users: recipients,
      suppliers: suppliers,
      projectSlug: currentProject.slug,
      emailText,
    }

    await ServiceErfa.submitErfa(erfaObj);
    setSubmitting(false);
    NotificationManager.success(t('notification.success.submit', 'Skickat'));
    goBack();
  }

  const breadcrumb = useMemo(() => {
    return [currentProject.name, t('pageTitle', 'Erfarenhetsåterföring')];
  }, [t, currentProject]);

  return (
    <AppLayout title={
      <BackButton
        onClick={goBack}
        path={breadcrumb}
      />
    }>
      <div className={styles.container}>
        <div className={styles.content}>
          {renderSuppliers()}
          {renderRecipients()}
          {renderQuestions()}
          {renderEmailViewer()}
          <TooltipContainer
            renderReferenceComponent={(className, ref) => (
              <div style={{ alignSelf: 'flex-end' }} ref={ref} className={className}>
                <BtButton
                  loaderShow={submitting}
                  onClick={handleSubmit}
                  disabled={!!validationMessage}
                >
                  {t('button.submit', 'Skicka')}
                </BtButton>
              </div>
          )}>
            {validationMessage && (
              <span style={{ maxWidth: '310px', width: 'max-content' }}>{validationMessage}</span>
            )}
          </TooltipContainer>
        </div>
      </div>
    </AppLayout>
  );
}

export default PageErfaRequest;
